/**
 * Iframe Diagnostics Utility
 * 
 * This utility helps identify the source of malicious iframes by:
 * 1. Tracking when iframes are added to the DOM
 * 2. Logging stack traces to identify the code that created them
 * 3. Monitoring script execution
 * 4. Detecting browser extensions
 */

// Track iframe creation
export const setupIframeDiagnostics = () => {
    console.log('🔍 Iframe Diagnostics: Starting monitoring...');

    // Override iframe creation methods
    const originalCreateElement = document.createElement.bind(document);
    document.createElement = function (tagName, options) {
        const element = originalCreateElement(tagName, options);

        if (tagName.toLowerCase() === 'iframe') {
            const stackTrace = new Error().stack;
            console.warn('⚠️ IFRAME CREATED via document.createElement:', {
                timestamp: new Date().toISOString(),
                stackTrace: stackTrace,
                element: element
            });

            // Watch for when styles are applied (only log if not from legitimate sources)
            const originalSetAttribute = element.setAttribute.bind(element);
            element.setAttribute = function (name, value) {
                // Only log if it's not from Google OAuth or other legitimate sources
                const isLegitimate = name === 'src' && value && (
                    value.includes('accounts.google.com') ||
                    value.includes('oauth.googleusercontent.com') ||
                    value.includes('www.facebook.com') ||
                    value.includes('www.youtube.com')
                );

                if ((name === 'style' || name === 'src') && !isLegitimate) {
                    console.warn('⚠️ IFRAME ATTRIBUTE SET:', {
                        attribute: name,
                        value: value,
                        stackTrace: new Error().stack
                    });
                }
                return originalSetAttribute(name, value);
            };

            // Watch for style property changes
            let styleProxy = new Proxy(element.style, {
                set: function (target, property, value) {
                    if (property === 'zIndex' || property === 'z-index' || property === 'position') {
                        console.warn('⚠️ IFRAME STYLE SET:', {
                            property: property,
                            value: value,
                            stackTrace: new Error().stack
                        });
                    }
                    target[property] = value;
                    return true;
                }
            });

            // Monitor when iframe is added to DOM
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        console.warn('⚠️ IFRAME ADDED TO DOM:', {
                            timestamp: new Date().toISOString(),
                            parent: element.parentNode,
                            stackTrace: new Error().stack
                        });
                    }
                });
            });

            // Start observing when element gets a parent
            setTimeout(() => {
                if (element.parentNode) {
                    observer.observe(element.parentNode, { childList: true });
                }
            }, 0);
        }

        return element;
    };

    // Monitor appendChild calls
    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function (child) {
        if (child && child.tagName === 'IFRAME') {
            console.warn('⚠️ IFRAME APPENDED via appendChild:', {
                timestamp: new Date().toISOString(),
                parent: this,
                child: child,
                stackTrace: new Error().stack
            });
        }
        return originalAppendChild.call(this, child);
    };

    // Monitor insertBefore calls
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode, referenceNode) {
        if (newNode && newNode.tagName === 'IFRAME') {
            console.warn('⚠️ IFRAME INSERTED via insertBefore:', {
                timestamp: new Date().toISOString(),
                parent: this,
                newNode: newNode,
                stackTrace: new Error().stack
            });
        }
        return originalInsertBefore.call(this, newNode, referenceNode);
    };

    // Monitor innerHTML assignments that might create iframes
    const originalInnerHTMLSetter = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML').set;
    Object.defineProperty(Element.prototype, 'innerHTML', {
        set: function (value) {
            if (typeof value === 'string' && value.toLowerCase().includes('<iframe')) {
                console.warn('⚠️ IFRAME CREATED via innerHTML:', {
                    timestamp: new Date().toISOString(),
                    element: this,
                    value: value.substring(0, 200), // First 200 chars
                    stackTrace: new Error().stack
                });
            }
            return originalInnerHTMLSetter.call(this, value);
        },
        get: Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML').get
    });

    // Log all scripts when diagnostics start
    console.log('📜 Currently loaded scripts:',
        Array.from(document.querySelectorAll('script')).map(s => ({
            src: s.src || 'inline',
            id: s.id,
            type: s.type,
            async: s.async,
            defer: s.defer
        }))
    );

    // Detect browser extensions
    if (window.chrome && window.chrome.runtime) {
        console.log('🔌 Chrome browser detected - extensions may be present');
    }

    // Monitor for dynamically loaded scripts
    const scriptObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.tagName === 'SCRIPT') {
                    console.warn('⚠️ NEW SCRIPT LOADED:', {
                        src: node.src || 'inline',
                        id: node.id,
                        type: node.type,
                        timestamp: new Date().toISOString()
                    });
                }
            });
        });
    });

    scriptObserver.observe(document.head, { childList: true, subtree: true });
    scriptObserver.observe(document.body, { childList: true, subtree: true });

    console.log('✅ Iframe Diagnostics: Monitoring active');
};

// Auto-initialize diagnostics in development mode
if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupIframeDiagnostics);
    } else {
        setupIframeDiagnostics();
    }
}

